import { useState, useEffect } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import PageHeader from "@/components/molecules/PageHeader";
import Button from "@/components/atoms/Button";
import { ArrowUpDown, Plus } from "lucide-react";
import Link from "@/components/atoms/Link";
import Badge from "@/components/atoms/Badge";
import SearchBox from "@/components/molecules/SearchBox";
import Container from "@/components/atoms/Container";
import useAccess from "@/hooks/useAccess";
import User from "@/types/User";
import { Account } from "@/types/Account";
import UserFilter from "@/components/organisms/User/Filter";
import * as Constants from "@/constants";
import Pagination from "@/components/atoms/Pagination";
import axios from "axios";
import { X } from "lucide-react";
import Role from "@/types/Role";
import { router } from "@inertiajs/react";

type Props = {
  accounts: Account[];
  roles: Role[];
};

const columns: ColumnDef<User>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <ColumnHeader column={column} sorted>
        Name
      </ColumnHeader>
    ),
    cell: ({ row }) => (
      <div className="flex items-center space-x-2">
        <img
          src={row.original.avatar_url}
          alt={row.original.name}
          className="w-8 h-8 rounded-full dark:bg-darkTextColor"
        />
        <span>{row.original.name}</span>
      </div>
    ),
  },
  {
    id: "email",
    accessorKey: "email",
    header: ({ column }) => <ColumnHeader column={column}>Email</ColumnHeader>,
  },
  {
    id: "roles",
    accessorKey: "roles",
    header: "Roles",
    cell: ({ row }) => (
      <div>{row.original.roles.map(({ name }) => name).join(", ")}</div>
    ),
  },
  {
    id: "status",
    accessorKey: "status",
    header: ({ column }) => <ColumnHeader column={column}>Status</ColumnHeader>,
    cell: ({ row }) => {
      const status = row.original.status;
      const badgeStatus =
        status === Constants.STATUSES.ACTIVE ? "success" : "destructive";

      return (
        <div className="space-x-2">
          <Badge variant={badgeStatus} className="p-1" />
          <span>{status}</span>
        </div>
      );
    },
  },
];

const Index = ({ accounts, roles }: Props) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showResetFilter, setshowResetFilter] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [exportMode, setExportMode] = useState<boolean>(false);
  const [exportButtonText, setExportButtonText] = useState<string>("Export");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const { getAccess } = useAccess();
  const breadcrumbItems = [
    {
      label: "Users",
      onClick: () => router.get("/admin/users"),
    },
  ];
  

  useEffect(() => {
    const fetchUsers = async () => {
      const queryParams: Record<string, any> = {
        ...filters,
        ...pagination,
      };

      if (search) {
        queryParams.search = search;
      }

      const response = await axios.get('/admin/users/listing', {
        params: queryParams
      });
    
      setUsers(response.data.users);
      setTotalCount(response.data.total_count);
      setTotalPages(response.data.total_pages);
      
      if (response.data.current_page !== pagination.page) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    };

    fetchUsers();
  }, [filters, pagination, search]);


  const handleFilterChange = (filterId: string, value: any) => {
    if (value !== null) {
      setshowResetFilter(true);
    }

    if (filterId === 'account_ids') {
      setSelectedAccounts(value as string[]);
    }

    setFilters(prev => ({ ...prev, [filterId]: value }));
  };

  const removeTag = (value: string) => {
    setSelectedAccounts(selectedAccounts.filter((item) => item !== value));
  };

  const handlePageChange = (page: number) => {  
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSearch = (search: string) => {
    setshowResetFilter(true);
    setSearch(search);
    setPagination({ page: 1, per_page: 10 });
  };

  const resetFilters = () => {
    handleFilterChange('account_ids', null);
    handleFilterChange('country_ids', null);
    handleFilterChange('role_ids', null);
    handleFilterChange('status', null);
    setshowResetFilter(false);
    setPagination({ page: 1, per_page: 10 });
    setSearch("");
  };

  const handleExport = () => {
    const exportUsers = async () => {
      setExportMode(true);
      const queryParams: Record<string, any> = {
        ...filters
      };

      await axios.get('/admin/users/export', {
        params: queryParams
      }).then(response => {
        if (response.status === 200 && response.data && response.data.jid) {
          const jobId = response.data.jid;

          setExportButtonText('Exporting...');
          checkExportStatus(jobId);
        }
      })
      .catch(error => {
        console.error('Error exporting data:', error);
      });
    };

    exportUsers();
  }

  const checkExportStatus = async (jobId: string) => {
    try {
      const response = await axios.get(`/admin/users/export_status/${jobId}`);
    
      if (response.data.status === 'complete') {
        setExportMode(false);
        setExportButtonText('Export');
        window.location.href = `/admin/users/export_download/${jobId}`;
      } else if(response.data.status === 'failed') {
        setExportMode(false);
        setExportButtonText('Export');
        alert('Export failed. Please contact administrator.');
      } else {
        if (response.data.percentage) {
          setExportButtonText(`Exporting... ${response.data.percentage}%`);
        }

        setTimeout(() => checkExportStatus(jobId), 1000);
      }
    } catch (error) {
      console.error('Error checking export status:', error);
    }
  };

  return (
    <>
      <Container>
        <PageHeader title="Users" breadcrumbItems={breadcrumbItems}>
          <div className="space-x-2">
          {getAccess(["create_user"]) ? (
            <Link href="/admin/users/new">
              <Button className="inline-flex items-center gap-2 text-black">
                <Plus size={16} /> <span>Add User</span>
              </Button>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 text-gray-500 cursor-not-allowed">
              <Button className="inline-flex items-center gap-2 text-black" disabled>
                <Plus size={16} /> <span>Add User</span>
              </Button>
            </span>
          )}
          </div>
        </PageHeader>
        
        <div className="flex items-center justify-between pt-6 pb-4">
          <SearchBox onSearch={handleSearch} value={search} />

          <div className="flex gap-1">
            <Button
              variant="outline"
              className="inline-flex items-center gap-2 border-border"
              onClick={handleExport}
              disabled={!getAccess(["export_users"]) && exportMode}
            >
              <ArrowUpDown size={16} /> <span>{exportButtonText}</span>
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {(selectedAccounts || []).map((item) => (
            <span key={item} className="bg-gray-200 dark:bg-primary dark:text-black px-2 py-1 rounded flex items-center gap-1 text-xs">
              {accounts.find((opt) => opt.id.toString() == item)?.name}
              <button onClick={() => removeTag(item)}>
                <X size={16} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-6 pb-4">
          <UserFilter 
            filters={[
              {
                id: "account_ids",
                label: "By Account",
                options: accounts.map(account => ({ value: account.id, label: account.name, countries: account.countries })),
                selected: selectedAccounts
              },
              {
                id: "role_ids",
                label: "By Role",
                options: roles.map(role => ({ value: role.id, label: role.name }))
              },
              {
                id: "status",
                label: "By Status",
                options: [
                  {
                    value: "Active",
                    label: "ACTIVE"
                  },
                  {
                    value: "Inactive",
                    label: "INACTIVE"
                  }
                ]
              }
            ]}
            selectedFilters={filters}
            onFilterChange={handleFilterChange}
          />
          {showResetFilter && (
            <div className="flex flex-col">
              <Button onClick={resetFilters}>Reset Filters</Button>
            </div>
          )}
          
        </div>
        <div className="border-b border-border" />
        <DataTable
          columns={columns}
          data={users}
          rowUrl={(row) => `${location.pathname}/${row.id}`}
        />
        <Pagination
          currentPage={pagination.page}
          totalPages={totalPages}
          totalCount={totalCount}
          perPage={pagination.per_page}
          onPageChange={handlePageChange}
        />
      </Container>
      
    </>
  );
};

export default Index;
